import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import {
  Upload,
  FileText,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Loader2,
  ArrowRight,
  Target,
  Sparkles,
  TrendingUp,
  FileCheck,
  X,
  RefreshCw,
  Mail,
  Shield,
  Zap,
  Award,
  BarChart3,
  Clock,
  Users
} from 'lucide-react';

const CVReviewPage = () => {
  const { user, isAuthenticated, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    
    // Require authentication before allowing file upload
    if (!isAuthenticated) {
      setError('Please sign in to use the CV Review service');
      return;
    }
    
    const droppedFile = e.dataTransfer?.files?.[0] || e.target.files?.[0];
    
    if (droppedFile) {
      const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(droppedFile.type)) {
        setError('Please upload a PDF or DOCX file');
        return;
      }
      if (droppedFile.size > 10 * 1024 * 1024) {
        setError('File must be less than 10MB');
        return;
      }
      setFile(droppedFile);
      setError(null);
    }
  }, [isAuthenticated]);

  const handleAnalyze = async () => {
    if (!file) return;
    
    // Strict authentication check
    if (!isAuthenticated || !user) {
      setError('Please sign in to analyze your CV');
      return;
    }

    setAnalyzing(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('session_token');
      if (!token) {
        throw new Error('Authentication required. Please sign in.');
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cv-review/analyze`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Analysis failed');
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      console.error('CV Analysis error:', err);
      setError(err.message || 'Failed to analyze CV. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const resetAnalysis = () => {
    setFile(null);
    setResult(null);
    setError(null);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-500';
    if (score >= 60) return 'text-amber-500';
    return 'text-red-500';
  };

  const getScoreGradient = (score) => {
    if (score >= 80) return 'from-emerald-500 to-teal-500';
    if (score >= 60) return 'from-amber-500 to-orange-500';
    return 'from-red-500 to-rose-500';
  };

  const getProgressColor = (score) => {
    if (score >= 80) return 'bg-gradient-to-r from-emerald-500 to-teal-500';
    if (score >= 60) return 'bg-gradient-to-r from-amber-500 to-orange-500';
    return 'bg-gradient-to-r from-red-500 to-rose-500';
  };

  return (
    <Layout>
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-slate-900 via-primary-900 to-slate-900 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-violet-500/20 rounded-full blur-3xl" />
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6">
              <Zap className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-medium text-white/90">AI-Powered Analysis</span>
            </div>
            
            {/* Title */}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Free CV Review &{' '}
              <span className="bg-gradient-to-r from-primary-400 to-violet-400 bg-clip-text text-transparent">
                ATS Scanner
              </span>
            </h1>
            
            {/* Subtitle */}
            <p className="text-lg sm:text-xl text-white/70 max-w-2xl mx-auto mb-8">
              Get instant feedback on your CV. Our AI analyzes your resume against ATS systems 
              and provides actionable recommendations to boost your job applications.
            </p>
            
            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-8 mb-8">
              <div className="flex items-center gap-2 text-white/80">
                <Users className="w-5 h-5 text-primary-400" />
                <span className="text-sm">50,000+ CVs Analyzed</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <Clock className="w-5 h-5 text-primary-400" />
                <span className="text-sm">Results in 30 seconds</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <Shield className="w-5 h-5 text-primary-400" />
                <span className="text-sm">100% Secure & Private</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-neutral-50 min-h-screen">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 pb-16 relative z-10">
          
          {!result ? (
            <>
              {/* Upload Card */}
              <Card className="shadow-2xl border-0 overflow-hidden">
                <CardContent className="p-0">
                  {/* Sign In Required Banner for Non-authenticated users */}
                  {!isAuthenticated && (
                    <div className="p-6 bg-gradient-to-r from-primary-50 to-violet-50 border-b">
                      <div className="flex flex-col sm:flex-row items-center gap-4">
                        <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Shield className="w-6 h-6 text-primary-600" />
                        </div>
                        <div className="flex-1 text-center sm:text-left">
                          <h3 className="font-semibold text-neutral-900 text-lg">Sign In Required</h3>
                          <p className="text-sm text-neutral-600">Create a free account or sign in to use our CV Review service</p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                          <Button
                            onClick={loginWithGoogle}
                            variant="outline"
                            className="h-11 px-5 gap-2"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24">
                              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                            Continue with Google
                          </Button>
                          <Button
                            onClick={() => navigate('/login?redirect=/cv-review')}
                            className="h-11 px-5 bg-gradient-to-r from-primary-600 to-violet-600"
                          >
                            Sign In / Register
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Upload Area */}
                  <div className="p-8">
                    {!file ? (
                      <label
                        className={`flex flex-col items-center justify-center w-full min-h-[320px] border-2 border-dashed rounded-2xl transition-all duration-300 ${
                          !isAuthenticated 
                            ? 'border-neutral-200 bg-neutral-50 cursor-not-allowed opacity-60'
                            : dragActive 
                              ? 'border-primary-500 bg-primary-50 scale-[1.02] cursor-pointer' 
                              : 'border-neutral-300 hover:border-primary-400 hover:bg-primary-50/50 cursor-pointer'
                        }`}
                        onDragOver={(e) => { 
                          e.preventDefault(); 
                          if (isAuthenticated) setDragActive(true); 
                        }}
                        onDragLeave={() => setDragActive(false)}
                        onDrop={handleFileDrop}
                        data-testid="cv-upload-area"
                      >
                        <div className="flex flex-col items-center justify-center py-8">
                          {/* Animated Icon */}
                          <div className={`relative mb-6 transition-transform duration-300 ${dragActive ? 'scale-110' : ''}`}>
                            <div className={`w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg ${
                              isAuthenticated 
                                ? 'bg-gradient-to-br from-primary-500 to-violet-500 shadow-primary-500/30' 
                                : 'bg-neutral-300'
                            }`}>
                              <Upload className="w-10 h-10 text-white" />
                            </div>
                            {isAuthenticated && (
                              <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center border-4 border-white">
                                <Sparkles className="w-4 h-4 text-white" />
                              </div>
                            )}
                          </div>
                          
                          <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                            {!isAuthenticated 
                              ? 'Sign in to upload your CV' 
                              : dragActive 
                                ? 'Drop your CV here' 
                                : 'Upload your CV'}
                          </h3>
                          <p className="text-neutral-600 mb-6">
                            {isAuthenticated 
                              ? <>Drag and drop or <span className="text-primary-600 font-medium">click to browse</span></>
                              : 'Create an account or sign in above to get started'}
                          </p>
                          
                          <div className="flex items-center gap-4">
                            <Badge variant="outline" className={`px-4 py-1.5 text-sm ${isAuthenticated ? 'bg-white' : 'bg-neutral-100'}`}>
                              <FileText className="w-4 h-4 mr-2" />
                              PDF
                            </Badge>
                            <Badge variant="outline" className={`px-4 py-1.5 text-sm ${isAuthenticated ? 'bg-white' : 'bg-neutral-100'}`}>
                              <FileText className="w-4 h-4 mr-2" />
                              DOCX
                            </Badge>
                            <span className="text-xs text-neutral-500">Max 10MB</span>
                          </div>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          onChange={handleFileDrop}
                          disabled={!isAuthenticated}
                        />
                      </label>
                    ) : (
                      <div className="bg-gradient-to-r from-primary-50 to-violet-50 rounded-2xl p-6 border border-primary-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-violet-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/20">
                              <FileText className="w-7 h-7 text-white" />
                            </div>
                            <div>
                              <p className="font-semibold text-neutral-900 text-lg">{file.name}</p>
                              <p className="text-sm text-neutral-600">
                                {(file.size / 1024 / 1024).toFixed(2)} MB • Ready to analyze
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setFile(null)}
                            className="text-neutral-400 hover:text-red-500 hover:bg-red-50"
                          >
                            <X className="w-5 h-5" />
                          </Button>
                        </div>
                      </div>
                    )}

                    {error && (
                      <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <p className="text-sm font-medium">{error}</p>
                      </div>
                    )}

                    {file && isAuthenticated && (
                      <div className="mt-8 flex justify-center">
                        <Button
                          size="lg"
                          onClick={handleAnalyze}
                          disabled={analyzing}
                          className="h-14 px-10 text-lg bg-gradient-to-r from-primary-600 to-violet-600 hover:from-primary-700 hover:to-violet-700 shadow-lg shadow-primary-500/30"
                          data-testid="analyze-cv-btn"
                        >
                          {analyzing ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin mr-3" />
                              Analyzing Your CV...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-5 h-5 mr-3" />
                              Analyze My CV
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Features Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
                <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/20">
                      <Target className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-semibold text-lg text-neutral-900 mb-2">ATS Compatibility</h3>
                    <p className="text-neutral-600">Check if your CV passes Applicant Tracking System filters used by 95% of employers</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-amber-500/20">
                      <BarChart3 className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-semibold text-lg text-neutral-900 mb-2">Keyword Analysis</h3>
                    <p className="text-neutral-600">Identify missing industry keywords that recruiters and ATS systems look for</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-violet-500 rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-primary-500/20">
                      <Award className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-semibold text-lg text-neutral-900 mb-2">Expert Recommendations</h3>
                    <p className="text-neutral-600">Get personalized tips to make your CV stand out from the competition</p>
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            /* Results Section */
            <div className="space-y-6">
              {/* Overall Score Card */}
              <Card className="overflow-hidden border-0 shadow-2xl">
                <div className={`bg-gradient-to-r ${getScoreGradient(result.overall_score)} p-1`}>
                  <div className="bg-white rounded-lg">
                    <CardContent className="p-8">
                      <div className="flex flex-col md:flex-row items-center gap-8">
                        {/* Score Circle */}
                        <div className="relative">
                          <div className="w-40 h-40 rounded-full bg-gradient-to-br from-neutral-100 to-neutral-50 flex items-center justify-center shadow-inner">
                            <div className={`text-6xl font-bold bg-gradient-to-r ${getScoreGradient(result.overall_score)} bg-clip-text text-transparent`}>
                              {result.overall_score}
                            </div>
                          </div>
                          <div className={`absolute -bottom-2 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-white text-sm font-medium bg-gradient-to-r ${getScoreGradient(result.overall_score)}`}>
                            out of 100
                          </div>
                        </div>
                        
                        {/* Score Details */}
                        <div className="flex-1 text-center md:text-left">
                          <h2 className="text-2xl font-bold text-neutral-900 mb-2">Your ATS Score</h2>
                          <p className="text-neutral-600 mb-4">
                            {result.overall_score >= 80 
                              ? "Excellent! Your CV is well-optimized for ATS systems."
                              : result.overall_score >= 60 
                              ? "Good start! With a few improvements, your CV will perform better."
                              : "Your CV needs significant improvements to pass ATS filters."}
                          </p>
                          <Badge 
                            className={`px-4 py-1.5 text-sm ${
                              result.overall_score >= 80 ? 'bg-emerald-100 text-emerald-700' :
                              result.overall_score >= 60 ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}
                          >
                            {result.overall_score >= 80 ? 'Excellent' :
                             result.overall_score >= 60 ? 'Needs Improvement' :
                             'Needs Work'}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </div>
                </div>
              </Card>

              {/* Section Scores */}
              <Card className="border-0 shadow-xl">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary-600" />
                    Section Breakdown
                  </CardTitle>
                  <CardDescription>Detailed analysis of each CV section</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5 pt-4">
                  {result.sections?.map((section, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-neutral-900">{section.name}</span>
                        <span className={`font-bold ${getScoreColor(section.score)}`}>
                          {section.score}/100
                        </span>
                      </div>
                      <div className="w-full bg-neutral-100 rounded-full h-3 overflow-hidden">
                        <div
                          className={`h-3 rounded-full ${getProgressColor(section.score)} transition-all duration-500`}
                          style={{ width: `${section.score}%` }}
                        />
                      </div>
                      <p className="text-sm text-neutral-600">{section.feedback}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Keyword Analysis */}
              {result.keywords && (
                <Card className="border-0 shadow-xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-primary-600" />
                      Keyword Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="bg-emerald-50 rounded-xl p-5">
                        <h4 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5" />
                          Keywords Found ({result.keywords.found?.length || 0})
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {result.keywords.found?.map((kw, i) => (
                            <Badge key={i} className="bg-emerald-100 text-emerald-700 border-0">
                              {kw}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="bg-amber-50 rounded-xl p-5">
                        <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5" />
                          Missing Keywords ({result.keywords.missing?.length || 0})
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {result.keywords.missing?.map((kw, i) => (
                            <Badge key={i} variant="outline" className="border-amber-300 text-amber-700 bg-white">
                              {kw}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recommendations */}
              {result.recommendations && result.recommendations.length > 0 && (
                <Card className="border-0 shadow-xl overflow-hidden">
                  <div className="bg-gradient-to-r from-primary-500 to-violet-500 p-4">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Sparkles className="w-5 h-5" />
                      Recommendations to Improve Your Score
                    </CardTitle>
                  </div>
                  <CardContent className="pt-6">
                    <ul className="space-y-4">
                      {result.recommendations.map((rec, index) => (
                        <li key={index} className="flex gap-4 items-start">
                          <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-violet-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary-500/20">
                            <span className="text-sm font-bold text-white">{index + 1}</span>
                          </div>
                          <p className="text-neutral-700 pt-1">{rec}</p>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Formatting Issues */}
              {result.formatting_issues && result.formatting_issues.length > 0 && (
                <Card className="border-0 shadow-xl border-l-4 border-l-amber-500">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-amber-700">
                      <AlertTriangle className="w-5 h-5" />
                      Formatting Issues to Fix
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {result.formatting_issues.map((issue, index) => (
                        <li key={index} className="flex items-start gap-3 text-neutral-700">
                          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                          {issue}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={resetAnalysis} 
                  className="gap-2 h-12 px-6"
                  data-testid="analyze-another-btn"
                >
                  <RefreshCw className="w-5 h-5" />
                  Analyze Another CV
                </Button>
                <Button 
                  size="lg"
                  onClick={() => navigate('/multi-apply')} 
                  className="gap-2 h-12 px-6 bg-gradient-to-r from-primary-600 to-violet-600 hover:from-primary-700 hover:to-violet-700"
                >
                  Try Multi-Apply Service
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CVReviewPage;
